// SiteRecordFields — the site RECORD's field body (the sites-row columns),
// shared by the create wizard's first step and the Edit-site drawer so create
// and edit stay one control (the ADR-038 shared-field-group rule, like users'
// AccessBody). Three sub-sections mirror the detail page: Identity /
// Classification / Location. Field tiers per the locked model: name + cluster
// are the CREATE MINIMUM (asterisk); the seven activation fields carry a quiet
// "Needed to activate" line (they never block saving a draft); the rest is
// always optional.
//
// Every list the form picks from is the family's `Picker` over a source (ADR-080
// §3 · §7): each loads on the press, nothing while closed but the names of what
// is held; the current value is always shown, marked when it can no longer be
// chosen. The cluster is ONE picker over the clusters the viewer's scope
// reaches, under their zones as headings (the zone follows the cluster) — a
// cluster outside the viewer's own placing reach is offered greyed ("Outside
// your scope"; the server refuses it on save). The type sits under its TIER as
// a heading carrying the tier's live free seats (the seats belong to the tier,
// said once); zero warns under the field, never blocks a draft. The area is the
// NAVIGATOR inside the picker (ADR-080 §14 — the site's country's areas alone,
// walked one level at a time; the pick's path under its name): it depends on
// the country (`must_belong` — cleared and SAID when the country changes; the
// server refuses an area outside the country), and waits for a country.

import { useMemo } from 'react';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { z } from 'zod';

import { Alert } from '../../components/ui/Alert.js';
import { Button } from '../../components/ui/Button.js';
import { CoordinateField } from '../../components/ui/CoordinateField.js';
import { Input } from '../../components/ui/Input.js';
import { Label } from '../../components/ui/Label.js';
import { Toggle } from '../../components/ui/Toggle.js';
import { Picker } from '../../components/patterns/Picker.js';
import { PickerRowFace } from '../../components/ui/PickerList.js';
import { countryName, formatNumber } from '../../lib/format.js';
import { useProjectCountries } from '../../hooks/useProjectCountries.js';
import { siteClusterSource } from '../../hooks/useClusters.js';
import { siteTypesSource, useSiteTypeTiers, useSiteTypes } from '../../hooks/useSiteTypes.js';
import { sitePrioritiesSource } from '../../hooks/useSitePriorities.js';
import { siteCategoriesSource } from '../../hooks/useSiteCategories.js';
import { siteTagsSource } from '../../hooks/useSiteTags.js';
import { administrativeAreasByCountrySource } from '../../hooks/useAdministrativeAreas.js';
import { timezoneSource } from '../../hooks/useTimezones.js';
import type { CodeSource, OptionRow } from '../../hooks/useOptions.js';
import type { SiteDetail } from '@ems-app/schemas/sites/responses';
import type { UpdateSiteRequest } from '@ems-app/schemas/sites/requests';
import type { SiteTypeTier } from '@ems-app/schemas/site-types/responses';

/** The record slice of the wizard/edit form (wire-aligned names; coordinates
 *  composite per the CoordinateField contract). */
export interface SiteRecordValues {
  name: string;
  cluster_id: number | null;
  reference: string;
  reference_alt: string;
  site_type_id: number | null;
  site_priority_id: number | null;
  site_category_id: number | null;
  tag_ids: number[];
  is_indoor: boolean;
  country_iso_code: string | null;
  timezone_iana: string | null;
  coordinates: { latitude: number; longitude: number } | null;
  administrative_area_id: number | null;
}

export const SITE_RECORD_FIELDS: (keyof SiteRecordValues)[] = [
  'name',
  'cluster_id',
  'reference',
  'reference_alt',
  'site_type_id',
  'site_priority_id',
  'site_category_id',
  'tag_ids',
  'is_indoor',
  'country_iso_code',
  'timezone_iana',
  'coordinates',
  'administrative_area_id',
];

// Draft-lenient validation shared by the wizard + the Edit drawer: only name +
// cluster hard-gate (the create minimum); activation requirements are the
// SERVER's completeness gate — hints + the checklist, never form blockers.
export const siteRecordShape = {
  name: z.string().trim().min(1, ' ').max(32),
  cluster_id: z
    .number()
    .int()
    .positive()
    .nullable()
    .refine((v) => v !== null, { message: ' ' }),
  // reference is the site's required IDENTITY (ADR-056 §4 revised) — part of the
  // create minimum, like name + cluster; the wizard can seed it from the name.
  reference: z.string().trim().min(1, ' ').max(32),
  reference_alt: z.string().trim().max(32),
  site_type_id: z.number().nullable(),
  site_priority_id: z.number().nullable(),
  site_category_id: z.number().nullable(),
  tag_ids: z.array(z.number()),
  is_indoor: z.boolean(),
  country_iso_code: z.string().nullable(),
  timezone_iana: z.string().nullable(),
  coordinates: z.object({ latitude: z.number(), longitude: z.number() }).nullable(),
  administrative_area_id: z.number().nullable(),
};

/** A detail row → the record form values (blank defaults when creating). */
export function siteRecordValuesFrom(detail: SiteDetail | null): SiteRecordValues {
  return {
    name: detail?.name ?? '',
    cluster_id: detail?.cluster.id ?? null,
    reference: detail?.reference ?? '',
    reference_alt: detail?.reference_alt ?? '',
    site_type_id: detail?.site_type?.id ?? null,
    site_priority_id: detail?.site_priority?.id ?? null,
    site_category_id: detail?.site_category?.id ?? null,
    tag_ids: detail?.tags.map((tg) => tg.id) ?? [],
    is_indoor: detail?.is_indoor ?? false,
    country_iso_code: detail?.country_iso_code ?? null,
    timezone_iana: detail?.timezone_iana ?? null,
    coordinates:
      detail?.latitude != null && detail?.longitude != null
        ? { latitude: detail.latitude, longitude: detail.longitude }
        : null,
    administrative_area_id: detail?.administrative_area?.id ?? null,
  };
}

/** The record form values → the PATCH body ('' clears a nullable text field).
 *  The designations (type · priority) are NOT here — they are SCD-managed
 *  (ADR-060 §3): the wizard commits them via the designation endpoints. */
export function siteRecordPatch(v: SiteRecordValues): UpdateSiteRequest {
  return {
    name: v.name,
    cluster_id: v.cluster_id ?? undefined,
    reference: v.reference.trim() || null,
    reference_alt: v.reference_alt.trim() || null,
    is_indoor: v.is_indoor,
    latitude: v.coordinates?.latitude ?? null,
    longitude: v.coordinates?.longitude ?? null,
    country_iso_code: v.country_iso_code,
    timezone_iana: v.timezone_iana,
    administrative_area_id: v.administrative_area_id,
    tag_ids: v.tag_ids,
  };
}

/** The "needed to activate" tier marker: a quiet dot on the field's label,
 *  explained once by the legend at the top of the form (the asterisk means
 *  "can't SAVE without it"; this dot means "can't ACTIVATE without it"). */
function ActivationDot() {
  return (
    <span
      aria-hidden
      className="ms-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--color-text-disabled)] align-middle"
    />
  );
}
function activationLabel(text: string) {
  return (
    <span className="inline-flex items-center">
      {text}
      <ActivationDot />
    </span>
  );
}

/** A tier's heading in the type picker: its name — and, on a prepaid project, its free seats said once ("Simple ·
 *  3 seats free"); an esco project names the tier alone. */
export function tierHeading(tier: SiteTypeTier, t: TFunction): string {
  if (tier.seats_free === null) return tier.name;
  return `${tier.name} · ${tier.seats_free === 0 ? t('wizard.noSeatsFree') : t('wizard.seatsFree', { count: tier.seats_free })}`;
}

const asString = (v: number | null): string | null => (v == null ? null : String(v));
const asNumber = (v: string | null): number | null => (v == null ? null : Number(v));

export function SiteRecordFields({
  form,
  projectId,
  t,
  /** Show the "needed to activate" dots + legend. Only meaningful for a DRAFT (the
   *  tier is about reaching activation); an already-active/suspended/decommissioned
   *  site has cleared it, so the edit drawer passes false and the markers vanish. */
  activationMarkers = true,
  /** Render the type/priority pickers. The WIZARD keeps them (drafts classify
   *  here — committed via the designation endpoints, ADR-060 §3); the non-draft edit
   *  drawer passes false: a live site's classification changes only through the
   *  designation timeline. */
  designations = true,
  /** The category field is asked only of a project that HAS categories (a category is optional forever); the wizard knows
   *  from its own read (it pre-fills the default from the same list). */
  categories = true,
}: {
  // The record fields live at the host form's top level (the wizard form is a
  // superset — the cast at the call site is the shared-fields convention).
  form: UseFormReturn<SiteRecordValues>;
  projectId: string;
  t: TFunction;
  activationMarkers?: boolean;
  designations?: boolean;
  categories?: boolean;
}) {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  // Draft-only: with markers off, labels render plain (no dot) and the legend hides.
  const actLabel = (text: string) => (activationMarkers ? activationLabel(text) : text);

  // ── the sources (ADR-080 §3) — one per kind, memoised on their inputs; each loads on the press ──
  const clusterSource = useMemo(() => siteClusterSource(projectId, { outsideScope: t('common:scope.outside') }), [projectId, t]);
  const typeSource = useMemo(() => siteTypesSource(projectId), [projectId]);
  const prioritySource = useMemo(() => sitePrioritiesSource(projectId), [projectId]);
  const categorySource = useMemo(() => siteCategoriesSource(projectId), [projectId]);
  const tagSource = useMemo(() => siteTagsSource(projectId), [projectId]);
  const areaSource = useMemo(() => administrativeAreasByCountrySource(projectId), [projectId]);
  // The tiers and the types as the type picker already holds them (its own read, on the press; OBSERVED here, never
  // asked — nothing loads while the picker is closed): the tier headings carry the seats, the chosen type's tier
  // warns at zero.
  const tiers = useSiteTypeTiers(projectId, { enabled: false }).data ?? [];
  const types = useSiteTypes(projectId, { enabled: false }).data ?? [];
  const tierLabel = (code: string): string => {
    const tier = tiers.find((x) => x.code === code);
    return tier ? tierHeading(tier, t) : code;
  };
  const seatsOfType = (id: number | null): number | null | undefined => {
    if (id == null) return undefined;
    const code = types.find((ty) => ty.id === id)?.license_tier_code;
    return code === undefined ? undefined : (tiers.find((x) => x.code === code)?.seats_free ?? undefined);
  };

  // The project's countries, named by the runtime (a code source the form already holds — the wizard reads the same
  // list for its default; the edit drawer is mounted open).
  const countries = useProjectCountries(projectId);
  const countrySource = useMemo<CodeSource>(() => {
    const rows: OptionRow[] = (countries.data ?? [])
      .map((c) => ({ id: c.iso_code, label: countryName(c.iso_code, lang), pickable: true, group: null }))
      .sort((a, b) => a.label.localeCompare(b.label, lang));
    return { kind: 'code', key: `countries:${lang}:${rows.map((r) => r.id).join(',')}`, rows, pending: countries.isPending };
  }, [countries.data, countries.isPending, lang]);
  const country = form.watch('country_iso_code');
  const timezone = form.watch('timezone_iana');
  // Every zone the runtime knows, plus the one held (built once per held value).
  const zoneSource = useMemo(() => timezoneSource([timezone]), [timezone]);

  const e = form.formState.errors;

  return (
    <div className="space-y-6">
      {/* Legend for the quiet activation marker — the dot on the fields below (draft only). */}
      {activationMarkers && (
        <p className="flex items-center gap-1.5 text-xs text-[var(--color-text-secondary)]">
          <ActivationDot />
          {t('wizard.toActivate')}
        </p>
      )}
      {/* ── Identity ── */}
      <section className="space-y-4">
        <h3 className="text-sm font-medium text-[var(--color-text-primary)]">{t('detail.sections.identity')}</h3>
        <div>
          <Label htmlFor="sw-name" required>
            {t('wizard.fields.name')}
          </Label>
          <Input id="sw-name" {...form.register('name')} error={e.name?.message} />
        </div>
        <Controller
          control={form.control}
          name="cluster_id"
          render={({ field, fieldState }) => (
            <Picker
              source={clusterSource}
              label={t('filters.cluster')}
              required
              groupLabel={(zone) => zone}
              // The row never repeats its heading: the zone stays on the row for the matcher (typing a zone finds its
              // clusters) and is read as the group's heading; the row shows its name — and a refused row the reason,
              // as its description (the inactive word the family's own).
              renderRow={(row) => <PickerRowFace row={{ ...row, description: row.pickable ? undefined : t('common:scope.outside') }} />}
              value={asString(field.value)}
              onChange={(v) => field.onChange(asNumber(v))}
              error={fieldState.error ? ' ' : undefined}
              help={t('wizard.fields.clusterHelp')}
            />
          )}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="sw-ref" required>
              {t('detail.fields.reference')}
            </Label>
            <Input id="sw-ref" dir="ltr" {...form.register('reference')} error={e.reference?.message} />
            {/* Reference is required at create — offer to seed it from the name
                (a walk-up create may not have a code to hand yet). */}
            <Button
              variant="link"
              className="mt-1 text-xs"
              disabled={!form.watch('name')?.trim()}
              onClick={() =>
                form.setValue('reference', (form.getValues('name') ?? '').trim(), { shouldDirty: true, shouldValidate: true })
              }
            >
              {t('wizard.fields.refFromName')}
            </Button>
          </div>
          <div>
            <Label htmlFor="sw-refalt">{t('detail.fields.referenceAlt')}</Label>
            <Input id="sw-refalt" dir="ltr" {...form.register('reference_alt')} error={e.reference_alt?.message} />
          </div>
        </div>
      </section>

      {/* ── Classification ── */}
      <section className="space-y-4">
        <h3 className="text-sm font-medium text-[var(--color-text-primary)]">{t('detail.sections.classification')}</h3>
        {designations && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Controller
              control={form.control}
              name="site_type_id"
              render={({ field, fieldState }) => (
                <div>
                  <Picker
                    source={typeSource}
                    label={actLabel(t('filters.type'))}
                    ariaLabel={t('filters.type')}
                    groupLabel={tierLabel}
                    value={asString(field.value)}
                    onChange={(v) => field.onChange(asNumber(v))}
                    clearable
                    error={fieldState.error?.message}
                  />
                  {seatsOfType(field.value) === 0 && (
                    <Alert variant="warning" className="mt-2">
                      {t('wizard.seatWarning')}
                    </Alert>
                  )}
                </div>
              )}
            />
            <Controller
              control={form.control}
              name="site_priority_id"
              render={({ field, fieldState }) => (
                <Picker
                  source={prioritySource}
                  label={actLabel(t('filters.priority'))}
                  ariaLabel={t('filters.priority')}
                  value={asString(field.value)}
                  onChange={(v) => field.onChange(asNumber(v))}
                  clearable
                  error={fieldState.error?.message}
                />
              )}
            />
            {/* Category — the 4th designation axis (ADR-060 §3), optional forever: no
                activation dot, and it only ever rides a type+priority classification. */}
            {categories && (
              <Controller
                control={form.control}
                name="site_category_id"
                render={({ field }) => (
                  <Picker source={categorySource} label={t('filters.category')} value={asString(field.value)} onChange={(v) => field.onChange(asNumber(v))} clearable />
                )}
              />
            )}
          </div>
        )}
        <Controller
          control={form.control}
          name="tag_ids"
          render={({ field }) => (
            // Removable chips below the field (selectedDisplay="tags" — the family's pick-many face in a form).
            <Picker multiple selectedDisplay="tags" source={tagSource} label={t('filters.tag')} value={field.value.map(String)} onChange={(v) => field.onChange(v.map(Number))} />
          )}
        />
        <Controller
          control={form.control}
          name="is_indoor"
          render={({ field }) => (
            <Toggle label={t('wizard.fields.indoor')} checked={field.value} onChange={field.onChange} />
          )}
        />
      </section>

      {/* ── Location ── */}
      <section className="space-y-4">
        <h3 className="text-sm font-medium text-[var(--color-text-primary)]">{t('detail.sections.location')}</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <Controller
            control={form.control}
            name="country_iso_code"
            render={({ field }) => (
              <Picker source={countrySource} label={actLabel(t('detail.fields.country'))} ariaLabel={t('detail.fields.country')} value={field.value} onChange={field.onChange} clearable />
            )}
          />
          <Controller
            control={form.control}
            name="timezone_iana"
            render={({ field }) => (
              <Picker
                source={zoneSource}
                label={actLabel(t('detail.fields.timezone'))}
                ariaLabel={t('detail.fields.timezone')}
                value={field.value}
                onChange={field.onChange}
                clearable
                help={t('wizard.fields.timezoneHelp')}
              />
            )}
          />
        </div>
        <Controller
          control={form.control}
          name="coordinates"
          render={({ field }) => (
            <div>
              <CoordinateField
                value={field.value}
                onChange={field.onChange}
                label={actLabel(t('detail.fields.coordinates'))}
                hint={t('wizard.fields.coordinatesHint')}
                parsedLabel={t('wizard.fields.coordinatesParsed')}
                invalidLabel={t('wizard.fields.coordinatesInvalid')}
                placeholder={`${formatNumber(24.7136)}, ${formatNumber(46.6753)}`}
              />
            </div>
          )}
        />
        {/* The area: the site's country's areas on the navigator — waits for a country, clears and says it when the
            country changes (ADR-080 §9 must_belong); the server refuses an area outside the country. */}
        <Controller
          control={form.control}
          name="administrative_area_id"
          render={({ field }) => (
            <Picker
              source={areaSource}
              dependsOn={{ value: country, label: country ? countryName(country, lang) : undefined, case: 'must_belong' }}
              navigator={{ thisLevel: t('wizard.fields.areaLevel') }}
              label={actLabel(t('detail.fields.administrative_area'))}
              ariaLabel={t('detail.fields.administrative_area')}
              searchPlaceholder={t('wizard.fields.areaSearch')}
              value={asString(field.value)}
              onChange={(v) => field.onChange(asNumber(v))}
              disabled={!country}
              clearable
            />
          )}
        />
      </section>
    </div>
  );
}
